import React from "react";
import { TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  size?: "small" | "medium" | "large";
  disabled?: boolean;
  sx?: React.CSSProperties | any;
  "data-testid"?: string;
}

const SearchField: React.FC<SearchFieldProps> = React.memo(
  ({ value, onChange, placeholder = "Search...", size = "medium", disabled = false, sx, "data-testid": testId }) => {
    const getSizeStyles = () => {
      switch (size) {
        case "small":
          return {
            height: 36,
            paddingX: 1.5,
            fontSize: "0.75rem",
          };
        case "large":
          return {
            height: 56,
            paddingX: 2.5,
            fontSize: "1rem",
          };
        default: // medium
          return {
            height: 48,
            paddingX: 2,
            fontSize: "0.875rem",
          };
      }
    };

    const sizeStyles = getSizeStyles();

    return (
      <TextField
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
        disabled={disabled}
        inputProps={{
          "data-testid": testId || "search-field-input",
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
        sx={{
          /* Match management list rows (UserList / InvitationList use borderRadius 2). */
          borderRadius: 2,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            height: sizeStyles.height,
            paddingX: {
              xs: size === "medium" ? 1.25 : sizeStyles.paddingX,
              sm: sizeStyles.paddingX,
            },
            "& fieldset": {
              borderColor: "divider",
            },
            "&:hover fieldset": {
              borderColor: "primary.main",
            },
            "&.Mui-focused fieldset": {
              borderColor: "primary.main",
            },
            "& input": {
              padding: 0,
              fontSize: {
                xs: "16px",
                sm: sizeStyles.fontSize,
              },
              minWidth: 0,
              textOverflow: "ellipsis",
              "&::placeholder": {
                fontSize: {
                  xs: "16px",
                  sm: sizeStyles.fontSize,
                },
              },
            },
          },
          ...sx,
        }}
        aria-label="Search"
      />
    );
  }
);

export default SearchField;
